import { ChangeEvent, FC, KeyboardEvent, useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LocationState } from "models/nav";
import Routes from "models/routes";
import useAuth from "hooks/useAuth";
import { actions as foodItemOptionsActions } from "store/slices/food/foodItemOptionsSlice";
import { useAppDispatch } from "hooks/useReduxStore";
import LanguageMenu from "components/common/ui/LanguageMenu";
import {
  FooterLink,
  LanguageMenuCorner,
  LoginButton,
  LoginFooter,
  LoginPageContainer,
  LoginPageLink,
  LoginTextField,
  LoginTitle,
  LoginTitleSub,
} from "components/login/ui/loginPageStyles";

interface LoginContainerProps {}

const LoginContainer: FC<LoginContainerProps> = () => {
  const { t } = useTranslation();
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState)?.from?.pathname || "/";
  const auth = useAuth();

  const handleLoginClick = useCallback(() => {
    auth.signIn(username, password, () => {
      dispatch(foodItemOptionsActions.fetchAllFoodItemOptions());
      navigate(from, { replace: true });
    });
  }, [auth, dispatch, from, navigate, password, username]);

  const handleUsernameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setUsername(event.target.value);
    },
    [],
  );

  const handlePasswordChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setPassword(event.target.value);
    },
    [],
  );

  const handleKeypress = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter") {
        handleLoginClick();
      }
    },
    [handleLoginClick],
  );

  return (
    <LoginPageContainer>
      <LanguageMenuCorner>
        <LanguageMenu />
      </LanguageMenuCorner>
      <LoginTitle>Chef Jasper</LoginTitle>
      <LoginTitleSub>Platform</LoginTitleSub>
      <LoginTextField
        label={t("User Name")}
        value={username}
        onChange={handleUsernameChange}
        onKeyDown={handleKeypress}
      />
      <LoginTextField
        label={t("Password")}
        value={password}
        type="password"
        onChange={handlePasswordChange}
        onKeyDown={handleKeypress}
      />
      <LoginPageLink to={Routes.FORGOT_PASSWORD}>
        {t("Forgot password?")}
      </LoginPageLink>
      <LoginButton text={t("Sign in")} positive onClick={handleLoginClick} />
      <LoginFooter>
        <FooterLink
          href="https://www.chefjasper.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("Privacy Policy")}
        </FooterLink>
      </LoginFooter>
    </LoginPageContainer>
  );
};

export default LoginContainer;
